import { Button, Input, Label, Modal } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import {
  FundingOutreachFormController,
  MANUAL_MESSAGE_SOURCE_LABELS,
} from "components/Pages/Funding/FundingOutreachFormController";

const SOURCE_OPTIONS: Array<keyof typeof MANUAL_MESSAGE_SOURCE_LABELS> = ["phone", "sms", "pasted", "other"];

/** Modal to add a manual message (phone note, SMS, pasted email, etc.) with AI sentiment analysis. */
const FundingOutreachAddMessageModal = () => {
  const controller = FundingOutreachFormController.use();
  const { showAddMessageModal, addMessageSource, addMessageSubject, addMessageBody, addingMessage } =
    controller.state;

  if (!showAddMessageModal) return null;

  const handleClose = () => controller.actionCloseAddMessageModal();
  const handleSubmit = () => controller.actionSubmitAddMessage();

  return (
    <Modal
      title="Add message"
      className="flex max-h-[85vh] w-full min-w-[85vw] max-w-2xl flex-col overflow-hidden sm:min-w-[85vw] sm:w-full"
      onClose={handleClose}
      portal
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600">
          Store communications that didn&apos;t go through email (e.g. phone call notes, SMS, or an email
          copied from elsewhere) so they&apos;re in one place and visible to your team.
        </p>
        <div className="shrink-0">
          <Label>Source</Label>
          <select
            value={addMessageSource}
            onChange={(e) =>
              controller.actionSetAddMessageSource(
                e.target.value as keyof typeof MANUAL_MESSAGE_SOURCE_LABELS,
              )
            }
            disabled={addingMessage}
            className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
          >
            {SOURCE_OPTIONS.map((key) => (
              <option key={key} value={key}>
                {MANUAL_MESSAGE_SOURCE_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
        <div className="shrink-0">
          <Label htmlFor="add-message-subject">Subject (optional)</Label>
          <Input
            id="add-message-subject"
            value={addMessageSubject}
            onChange={(e) => controller.actionSetAddMessageSubject(e.target.value)}
            placeholder="e.g. Re: Funding request"
            className="mt-1 w-full"
            disabled={addingMessage}
          />
        </div>
        <div className="shrink-0">
          <Label htmlFor="add-message-body">Message</Label>
          <textarea
            id="add-message-body"
            value={addMessageBody}
            onChange={(e) => controller.actionSetAddMessageBody(e.target.value)}
            placeholder="Paste or type the message content…"
            className="mt-1 min-h-[180px] w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
            rows={8}
            disabled={addingMessage}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button kind="secondary" onClick={handleClose} disabled={addingMessage}>
            Cancel
          </Button>
          <Button
            kind="primary"
            onClick={handleSubmit}
            disabled={addingMessage || !addMessageBody.trim()}
            spinner={addingMessage}
          >
            Add message
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ApplicationView(FundingOutreachAddMessageModal);
